/**
 * Whether a hostname refers to the machine the browser is talking to locally.
 *
 * A deliberate mirror of `api/utils/validation/localMachineHost.ts`, which is the source of
 * truth and carries the full reasoning. It exists separately because the two projects do not
 * share a module graph, and it is duplicated rather than relaxed on one side only: a form that
 * refuses a URL the server would accept is the failure this fixes, and a form that accepts one
 * the server refuses is the same failure pointing the other way. Change both together.
 *
 * Used to relax the https requirement on a destination's base URL. That rule exists because the
 * credential travels on every request, so plaintext would put it on a network someone else could
 * be listening to. None of the names below reach such a network:
 *
 *   - Loopback proper (127.0.0.0/8, ::1, `localhost`, any `.localhost` name): the request cannot
 *     leave the machine.
 *   - `host.docker.internal`, Docker's alias for the host as seen from a container. The local
 *     stack needs it, since jobs run inside Lambda containers where `localhost` is the
 *     container's own loopback. In a deployed environment the name does not resolve, so it
 *     fails closed rather than silently sending a credential somewhere else.
 *
 * Everything else still requires https. `10.x`, `192.168.x` and `.local` are all reachable over
 * a network someone else can be on, which is exactly the case the rule is for.
 */
export function isLocalMachineHost(hostname) {
  // URL.hostname keeps the brackets on an IPv6 literal.
  const host = String(hostname || '').replace(/^\[/, '').replace(/\]$/, '').toLowerCase();

  if (host === 'localhost' || host.endsWith('.localhost')) return true;
  if (host === '::1') return true;
  if (host === 'host.docker.internal') return true;

  // The whole 127.0.0.0/8 block, not just 127.0.0.1.
  return /^127\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.test(host);
}

/**
 * Whether a destination base URL is safe to send a credential to.
 *
 * The shared half of both LMS credential forms' `isValid()`: https anywhere, or plaintext only
 * to this machine. Returns false for anything that will not parse, so a half-typed URL keeps the
 * Save button disabled rather than throwing.
 *
 * The scheme must be http or https, which is the one place this is deliberately stricter than
 * the server: `validateLocation` tests only "not https", so `ftp://localhost` slips past it. No
 * LMS is served over ftp, so tightening it here cannot block real work, and it keeps the rule
 * equal to the sentence it is meant to enforce. Worth fixing on the server too, separately.
 */
export function isCredentialSafeUrl(value) {
  try {
    const url = new URL(String(value || '').trim());
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return false;
    return url.protocol === 'https:' || isLocalMachineHost(url.hostname);
  } catch (e) {
    return false;
  }
}
